import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem } from '@/icons';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { Modal } from './Modal';
import { CustomToggle } from './CustomToggle';
import type { AlimonyPartyKey, AlimonyScenarioModel, CaseData } from '../types/case';
import { otherPartyOf } from '../utils/alimonyAnalysis';

export interface AlimonyScenarioSelectModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The case whose scenarios are being chosen from. */
    caseData?: CaseData | null;
    /** Given the scenarios to include, in the order they appear on the page. */
    onConfirm: (selectedLineIds: string[]) => void;
    zIndex?: number;
}

interface SelectableScenario {
    lineId: string;
    /** 1-based, matching the "Scenario N" heading on the page. */
    number: number;
    amount: number;
    payor: AlimonyPartyKey;
}

function formatCurrency(amount: number): string {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Which scenarios go into the Alimony Analysis PDF.
 *
 * Everything is on when it opens: the common case is "print what I am looking at", and a dialog
 * that starts with nothing chosen makes the user do work to get the obvious outcome.
 *
 * Scenarios with no alimony amount are offered too. A scenario at zero is a real comparison --
 * what each party is left with if no alimony is paid -- and leaving it out of the list would mean
 * the PDF could not show it.
 */
export const AlimonyScenarioSelectModal: React.FC<AlimonyScenarioSelectModalProps> = ({
    isOpen,
    onClose,
    caseData,
    onConfirm,
    zIndex,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    const partyNames = useMemo<Record<AlimonyPartyKey, string>>(() => ({
        partyA: caseData?.parties?.partyA?.personal?.firstName?.trim() || 'Party A',
        partyB: caseData?.parties?.partyB?.personal?.firstName?.trim() || 'Party B',
    }), [caseData?.parties?.partyA?.personal?.firstName, caseData?.parties?.partyB?.personal?.firstName]);

    const scenarios = useMemo<SelectableScenario[]>(() => {
        const all: AlimonyScenarioModel[] = caseData?.alimonyAnalysis?.scenarios ?? [];
        return all
            .map((scenario, index) => ({ scenario, number: index + 1 }))
            .filter(({ scenario }) => !!scenario.lineId)
            .map(({ scenario, number }) => ({
                lineId: scenario.lineId!,
                number,
                amount: scenario.monthlyAmount ?? 0,
                payor: scenario.payor ?? 'partyA',
            }));
    }, [caseData?.alimonyAnalysis?.scenarios]);

    const [selected, setSelected] = useState<Set<string>>(new Set());

    // Re-arm every time it opens: the case may have changed since last time, and a stale
    // selection would silently leave a scenario out of the next download.
    useEffect(() => {
        if (isOpen) {
            setSelected(new Set(scenarios.map((scenario) => scenario.lineId)));
        }
    }, [isOpen, scenarios]);

    const toggle = (lineId: string) => {
        setSelected((previous) => {
            const next = new Set(previous);
            if (next.has(lineId)) {
                next.delete(lineId);
            } else {
                next.add(lineId);
            }
            return next;
        });
    };

    const allSelected = scenarios.length > 0 && selected.size === scenarios.length;
    const noneSelected = selected.size === 0;

    // Keep the page's order rather than the order they were ticked in.
    const confirm = () => onConfirm(scenarios.filter((s) => selected.has(s.lineId)).map((s) => s.lineId));

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Alimony Analysis" zIndex={zIndex}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{ ...themedStyles.text.body, color: theme.colors.text.primary }}>
                    Choose the scenarios to include. The PDF compares the ones you pick side by side,
                    then gives each its own page.
                </div>

                {scenarios.length === 0 ? (
                    <div style={{
                        ...themedStyles.text.body,
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text.secondary,
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.md,
                        backgroundColor: theme.colors.surfaceSecondary,
                    }}>
                        This case has no alimony scenarios yet. Add one on the Alimony step and it
                        will appear here.
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => setSelected(allSelected ? new Set() : new Set(scenarios.map((s) => s.lineId)))}
                                style={{
                                    ...themedStyles.text.body,
                                    fontSize: theme.typography.fontSize.sm,
                                    background: 'none',
                                    border: 'none',
                                    color: theme.colors.primary,
                                    cursor: 'pointer',
                                    padding: 0,
                                }}
                            >
                                {allSelected ? 'Clear all' : 'Select all'}
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                            {scenarios.map((scenario) => (
                                <div
                                    key={scenario.lineId}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.md,
                                        padding: theme.spacing.sm,
                                        borderRadius: theme.borderRadius.sm,
                                        border: `1px solid ${theme.colors.border.default}`,
                                        backgroundColor: selected.has(scenario.lineId)
                                            ? theme.colors.surfaceSecondary
                                            : 'transparent',
                                    }}
                                >
                                    <CustomToggle
                                        checked={selected.has(scenario.lineId)}
                                        onChange={() => toggle(scenario.lineId)}
                                        label={`Scenario ${scenario.number}`}
                                        style={{ width: '100%', margin: 0 }}
                                        labelStyle={{ flex: 1, fontWeight: theme.typography.fontWeight.medium }}
                                    />
                                    <div style={{
                                        ...themedStyles.text.body,
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.secondary,
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {partyNames[scenario.payor]} → {partyNames[otherPartyOf(scenario.payor)]}
                                        {'  '}
                                        <strong style={{ color: theme.colors.text.primary }}>
                                            {formatCurrency(scenario.amount)}/mo
                                        </strong>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.md }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                            border: `1px solid ${theme.colors.border.default}`,
                            borderRadius: theme.borderRadius.md,
                            background: 'transparent',
                            color: theme.colors.text.primary,
                            cursor: 'pointer',
                            fontSize: theme.typography.fontSize.sm,
                        }}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={confirm}
                        disabled={noneSelected}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                            background: noneSelected ? theme.colors.border.default : theme.colors.primary,
                            color: theme.colors.text.inverse,
                            border: 'none',
                            borderRadius: theme.borderRadius.md,
                            cursor: noneSelected ? 'not-allowed' : 'pointer',
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            opacity: noneSelected ? 0.7 : 1,
                        }}
                    >
                        <FontAwesomeIcon icon={faGem} />
                        Download PDF
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default AlimonyScenarioSelectModal;
